import React, { ReactElement, useState } from "react";
import {
  Copy,
  Divider,
  Flex,
  Space,
  Table,
  TableAction,
  Tag,
  Typography,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import type { TransactionWithdrawDetailsViewModel } from "@payping/api";
import { useLocation, useNavigate } from "react-router-dom";
import { getBalance, getWithdraws } from "@payping/redux/actions";
import { submitQueryParams } from "@payping/utils/query-string";
import { WithdrawFilter } from "@/components/DashReport/withdrawTabFilter";
import { WithdrawsFilters, WithdrawsNumberFilters, WithdrawsStringFilters } from "@/utils/Sale/initialFilters";
import qs from "query-string";
import type { ColumnsType } from "@payping/web-components/components/table/types";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { getCorrectDate } from "@/utils/getCorrectDate";
import PageComponentForHandlingCrispInMobile from "@/components/mobile/HandlingCrispInMobile";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import WithdrawDetails from "../WithdrawDetails";
import { useWithdrawStatus } from "./_hooks";

const BalanceAndWithdraw: React.FC = () => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [detailCode, setDetailCode] = useState<string>();
  const parsedSearch: WithdrawsFilters = qs.parse(location.search);
  const list = useAppSelector((state) => state.withdraws.list);
  const listCount = useAppSelector((state) => state.withdraws.totalCount);
  const { clientId, fromDate, searchText, toDate }: WithdrawsStringFilters = qs.parse(location.search);
  const { pageNumber = 1, pageSize = 10 }: WithdrawsNumberFilters = qs.parse(location.search, { parseNumbers: true });
  const { statusText } = useWithdrawStatus();
  const dataTable: TransactionWithdrawDetailsViewModel[] = list.map((item, index) => ({ ...item, key: index }));

  const cols: ColumnsType<TransactionWithdrawDetailsViewModel> = [
    {
      dataIndex: "amount",
      className: "en-font",
      render(value, record) {
        const { color, text } = statusText((record as any).status);
        return (
          <Flex gap="small">
            <Flex align="center" gap="small">
              <Typography.Text>
                {formatLocalizedNumber({ value, options: { maximumFractionDigits: 2 } })}
              </Typography.Text>
              <CuToman width={24} height={24} />
            </Flex>
            <Tag color={color}>{text}</Tag>
          </Flex>
        );
      },
      title: t("withdraw.table.amount"),
    },
    {
      dataIndex: "repayDate",
      render(value) {
        return (
          <div className={"tw:flex tw:justify-start"}>
            <div className="ltr">
              {
                toJalali({
                  // TODO: Wrong date format in backend (missing timezone), remove getCorrectDate after fix
                  date: getCorrectDate(value),
                  toFormat: dateFormats.ui.jalaliDateTime,
                }).string
              }
            </div>
          </div>
        );
      },
      title: t("withdraw.table.date"),
    },
    {
      dataIndex: "code",
      render(_, tableData) {
        return (
          <Copy className="en-font" text={tableData.code || ""}>
            {tableData.code}
          </Copy>
        );
      },
      title: t("withdraw.table.code"),
    },
  ];

  const { loading: loadingList, retry } = useMessagedAsyncRetry(
    () =>
      dispatch(
        getWithdraws({ clientId, fromDate, limit: pageSize, offset: (pageNumber - 1) * pageSize, searchText, toDate }),
      ),
    [clientId, dispatch, fromDate, pageNumber, pageSize, searchText, toDate],
    { success: false, useProgress: true },
  );

  const { value: balance } = useMessagedAsyncRetry(() => dispatch(getBalance()), [dispatch], {
    useProgress: true,
  });

  const actions = (rowIndex: number): (ReactElement | null)[] => [
    <Typography.Link onClick={() => setDetailCode(dataTable[rowIndex]?.code)} key={0}>
      <TableAction label={t("common.detail")} />
    </Typography.Link>,
  ];
  return (
    <PageComponentForHandlingCrispInMobile>
      <Space orientation={"vertical"} size={"small"} className={"tw:w-full"}>
        <Typography.Title level={4}>{t("mySales.withdrawTab.title")}</Typography.Title>
        <Typography.Text>{t("mySales.withdrawTab.desc")}</Typography.Text>

        <Flex align="center">
          <Typography.Text
            strong
          >{`${formatLocalizedNumber({ value: balance ?? 0, options: { maximumFractionDigits: 2 } })}`}</Typography.Text>
          <CuToman width={24} height={24} />
        </Flex>
        <Divider />
        <WithdrawFilter loading={loadingList} retry={retry} />
        <Table
          loading={!dataTable.length ? loadingList : false}
          hasIdColumn
          dataSource={dataTable}
          columns={cols}
          actions={actions}
          pagination={{
            onChange: (page, newSize): void => {
              const newValues = {
                ...parsedSearch,
                pageNumber: page,
                pageSize: newSize,
              };
              submitQueryParams({
                navigate,
                newValues,
                parsedSearch,
                pathname: location.pathname,
                retry,
              });
            },
            onShowSizeChange: (_currentPage, newSize): void => {
              const newValues = {
                ...parsedSearch,
                pageNumber: 1,
                pageSize: newSize,
              };
              submitQueryParams({
                navigate,
                newValues,
                parsedSearch,
                pathname: location.pathname,
                retry,
              });
            },
            pageNumber,
            pageSize,
            showTotal: (total, range): string =>
              t("report.table.showTotal", { range: `${range[0]}-${range[1]}`, total }),
            total: listCount,
          }}
        />
      </Space>

      {detailCode ? <WithdrawDetails code={detailCode} setCode={setDetailCode} /> : null}
    </PageComponentForHandlingCrispInMobile>
  );
};

export default BalanceAndWithdraw;
